import { forwardRef, type CSSProperties } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseDividerProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

export const BaseDivider = forwardRef<HTMLDivElement, BaseDividerProps>(
  function BaseDivider(
    {
      orientation = 'horizontal',
      label,
      appearance,
      className,
      classNames,
      style,
      ...dividerProps
    },
    ref,
  ) {
    if (orientation !== 'horizontal' && orientation !== 'vertical') {
      throw new TypeError(
        'BaseDivider orientation must be "horizontal" or "vertical".',
      );
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const isVertical = orientation === 'vertical';
    const hasLabel = hasRenderableNode(label) && !isVertical;
    const rootStyle: CSSProperties = {
      ...theme.styles.divider,
      ...style,
    };

    if (hasLabel) {
      return (
        <div
          {...componentAttributes}
          {...dividerProps}
          ref={ref}
          role="separator"
          aria-orientation={orientation}
          data-monkeys-component="divider"
          data-slot="root"
          data-orientation={orientation}
          data-state="labeled"
          className={cn(
            'flex min-w-0 items-center',
            classNames?.root,
            className,
          )}
          style={rootStyle}
        >
          <div
            data-slot="line"
            data-orientation={orientation}
            className={cn(
              'min-w-0 flex-1 border-t',
              theme.slots.dividerRoot,
              classNames?.line,
            )}
          />
          <span
            data-slot="label"
            data-orientation={orientation}
            className={cn(theme.slots.dividerLabel, classNames?.label)}
          >
            {label}
          </span>
          <div
            data-slot="line"
            data-orientation={orientation}
            className={cn(
              'min-w-0 flex-1 border-t',
              theme.slots.dividerRoot,
              classNames?.line,
            )}
          />
        </div>
      );
    }

    return (
      <div
        {...componentAttributes}
        {...dividerProps}
        ref={ref}
        role="separator"
        aria-orientation={orientation}
        data-monkeys-component="divider"
        data-slot="root"
        data-orientation={orientation}
        data-state="unlabeled"
        className={cn(
          theme.slots.dividerRoot,
          isVertical ? 'min-h-full self-stretch border-l' : 'w-full border-t',
          classNames?.root,
          className,
        )}
        style={rootStyle}
      />
    );
  },
);
